import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Plus, Save, Trash2, Loader2, AlertCircle, Edit2, X, Check,
  MapPin, Tag, ChevronRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import { 
  getPriceZones, createPriceZone, updatePriceZone, deletePriceZone,
  getCodePrices, createCodePrice, updateCodePrice, deleteCodePrice 
} from '../../api/wholesale/companyPricing';
import { getSizeCategories } from '../../api/wholesale/sizeCategories';
import { getTileTypes } from '../../api/wholesale/tileTypes';

const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
  'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
  'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const CompanyPricing = ({ companyId, createdProductAttributes = null }) => {
  const [activeTab, setActiveTab] = useState('zones');
  const [zones, setZones] = useState([]);
  const [codePrices, setCodePrices] = useState([]);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Zone form state
  const [showZoneForm, setShowZoneForm] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({ zone_name: '', zone_code: '', states: [] });
  const [savingZone, setSavingZone] = useState(false);
  
  // Price form state
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [priceForm, setPriceForm] = useState({
    code_pattern: '', size_category_id: '', tile_type_id: '',
    description: '', is_black: false, zone_prices: {}
  });
  const [savingPrice, setSavingPrice] = useState(false);

  // View modal state
  const [viewingZone, setViewingZone] = useState(null);
  const [viewingPrice, setViewingPrice] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [zonesData, pricesData, sizesData, typesData] = await Promise.all([
        getPriceZones(companyId),
        getCodePrices(companyId),
        getSizeCategories(),
        getTileTypes()
      ]);
      setZones(Array.isArray(zonesData) ? zonesData : []);
      setCodePrices(Array.isArray(pricesData) ? pricesData : []);
      setSizeCategories(Array.isArray(sizesData) ? sizesData : []);
      setTileTypes(Array.isArray(typesData) ? typesData : []);
    } catch (err) {
      console.error('Error fetching pricing data:', err);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) fetchData();
  }, [companyId]);

  // ===== ZONE HANDLERS =====
  const handleSaveZone = async () => {
    if (!zoneForm.zone_name) {
      toast.error('Zone name is required');
      return;
    }
    setSavingZone(true);
    try {
      if (editingZone) {
        await updatePriceZone(companyId, editingZone.id, zoneForm);
        toast.success('Zone updated');
      } else {
        await createPriceZone(companyId, zoneForm);
        toast.success('Zone created');
      }
      setShowZoneForm(false);
      setEditingZone(null);
      setZoneForm({ zone_name: '', zone_code: '', states: [] });
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to save zone');
    } finally {
      setSavingZone(false);
    }
  };

  const handleDeleteZone = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.zone_name}"?`)) return;
    try {
      await deletePriceZone(companyId, zone.id);
      toast.success('Zone deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete zone');
    }
  };

  const editZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      zone_name: zone.zone_name,
      zone_code: zone.zone_code || '',
      states: zone.states || []
    });
    setShowZoneForm(true);
  };

  // ===== PRICE HANDLERS =====
  const handleSavePrice = async () => {
    if (!priceForm.code_pattern) {
      toast.error('Code pattern or tile code is required');
      return;
    }
    if (Object.keys(priceForm.zone_prices).length === 0) {
      toast.error('At least one zone price is required');
      return;
    }
    setSavingPrice(true);
    try {
      const data = {
        ...priceForm,
        size_category_id: priceForm.size_category_id || null,
        tile_type_id: priceForm.tile_type_id || null
      };
      
      if (editingPrice) {
        await updateCodePrice(companyId, editingPrice.id, data);
        toast.success('Price updated');
      } else {
        await createCodePrice(companyId, data);
        toast.success('Price saved');
      }
      setShowPriceForm(false);
      setEditingPrice(null);
      setPriceForm({ code_pattern: '', size_category_id: '', tile_type_id: '', description: '', is_black: false, zone_prices: {} });
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to save price');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleDeletePrice = async (price) => {
    if (!window.confirm(`Delete price for "${price.code_pattern}"?`)) return;
    try {
      await deleteCodePrice(companyId, price.id);
      toast.success('Price deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete price');
    }
  };

  const editPrice = (price) => {
    setEditingPrice(price);
    setPriceForm({
      code_pattern: price.code_pattern,
      size_category_id: price.size_category_id || '',
      tile_type_id: price.tile_type_id || '',
      description: price.description || '',
      is_black: price.is_black || false,
      zone_prices: price.zone_prices || {}
    });
    setShowPriceForm(true);
  };

  const toggleState = (state) => {
    setZoneForm(prev => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => setActiveTab('zones')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'zones' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <MapPin className="w-4 h-4" />
          Price Zones ({zones.length})
        </button>
        <button
          onClick={() => setActiveTab('prices')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'prices' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Tag className="w-4 h-4" />
          Code Prices ({codePrices.length})
        </button>
      </div>

      {/* Price Zones Tab */}
      {activeTab === 'zones' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Define geographic zones for pricing</p>
            <button
              onClick={() => { setShowZoneForm(true); setEditingZone(null); setZoneForm({ zone_name: '', zone_code: '', states: [] }); }}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Zone
            </button>
          </div>

          {showZoneForm && (
            <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zone Name *</label>
                  <input
                    type="text"
                    value={zoneForm.zone_name}
                    onChange={(e) => setZoneForm(p => ({ ...p, zone_name: e.target.value }))}
                    placeholder="e.g., South-West Zone, Northern Zone"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zone Code</label>
                  <input
                    type="text"
                    value={zoneForm.zone_code}
                    onChange={(e) => setZoneForm(p => ({ ...p, zone_code: e.target.value.toUpperCase() }))}
                    placeholder="Auto-generated (e.g., SW)"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave empty to auto-generate from zone name</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">States in this zone</label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-white rounded-lg border">
                  {NIGERIAN_STATES.map(state => (
                    <button
                      key={state}
                      onClick={() => toggleState(state)}
                      className={`px-2 py-1 text-xs rounded-full transition-colors ${
                        zoneForm.states.includes(state)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {state}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveZone} disabled={savingZone} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {savingZone ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingZone ? 'Update' : 'Save'}
                </button>
                <button onClick={() => { setShowZoneForm(false); setEditingZone(null); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {zones.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No price zones configured</p>
            </div>
          ) : (
            <div className="space-y-2">
              {zones.map(zone => (
                <div 
                  key={zone.id} 
                  onClick={() => setViewingZone(zone)}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100 hover:border-blue-300 transition-colors"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{zone.zone_name}</span>
                      {zone.zone_code && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{zone.zone_code}</span>}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {zone.states?.length ? zone.states.join(', ') : 'No states assigned'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Code Prices Tab */}
      {activeTab === 'prices' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Set prices per code pattern or tile code for each zone</p>
            <button
              onClick={() => { setShowPriceForm(true); setEditingPrice(null); setPriceForm({ code_pattern: '', size_category_id: '', tile_type_id: '', description: '', is_black: false, zone_prices: {} }); }}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Price
            </button>
          </div>

          {showPriceForm && (
            <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code Patterns / Tile Codes *</label>
                  <input
                    type="text"
                    value={priceForm.code_pattern}
                    onChange={(e) => setPriceForm(p => ({ ...p, code_pattern: e.target.value.toUpperCase() }))}
                    placeholder="33PM, 33AT, 22BL or 33PM045"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Separate multiple patterns with commas (e.g., 33PM, 33AT, 22BL)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size Category</label>
                  <select
                    value={priceForm.size_category_id}
                    onChange={(e) => setPriceForm(p => ({ 
                      ...p, 
                      size_category_id: e.target.value,
                      tile_type_id: '' // Reset tile type when category changes
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {sizeCategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tile Type</label>
                  <select
                    value={priceForm.tile_type_id}
                    onChange={(e) => setPriceForm(p => ({ ...p, tile_type_id: e.target.value }))}
                    disabled={!priceForm.size_category_id}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">
                      {!priceForm.size_category_id
                        ? 'Select Size Category first'
                        : (createdProductAttributes && createdProductAttributes.length > 0
                            ? 'Select...'
                            : 'No products created yet')}
                    </option>
                    {tileTypes
                      .filter(t => {
                        // Filter by size category
                        if (priceForm.size_category_id && t.size_category_id !== parseInt(priceForm.size_category_id)) {
                          return false;
                        }
                        // Filter by created products - only show tile types used in products for this size category
                        if (createdProductAttributes && createdProductAttributes.length > 0 && priceForm.size_category_id) {
                          return createdProductAttributes.some(attr =>
                            attr.size_category_id == priceForm.size_category_id &&
                            attr.tile_type_id == t.id
                          );
                        }
                        return false; // No products = no tile types to show
                      })
                      .map(t => <option key={t.id} value={t.id}>{t.name}</option>)
                    }
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={priceForm.description}
                    onChange={(e) => setPriceForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Gold Series"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zone Prices</label>
                {zones.length === 0 ? (
                  <p className="text-sm text-orange-600">No zones configured. Add zones first.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {zones.map(zone => (
                      <div key={zone.id}>
                        <label className="block text-xs text-gray-500 mb-1">{zone.zone_name}</label>
                        <input
                          type="number"
                          value={priceForm.zone_prices[zone.id] || ''}
                          onChange={(e) => setPriceForm(p => ({
                            ...p,
                            zone_prices: { ...p.zone_prices, [zone.id]: parseFloat(e.target.value) || 0 }
                          }))}
                          placeholder="₦0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={priceForm.is_black}
                    onChange={(e) => setPriceForm(p => ({ ...p, is_black: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Black tile (premium)</span>
                </label>
              </div>

              <div className="flex gap-2">
                <button onClick={handleSavePrice} disabled={savingPrice} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingPrice ? 'Update' : 'Save'}
                </button>
                <button onClick={() => { setShowPriceForm(false); setEditingPrice(null); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {codePrices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No code prices configured</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-3 font-semibold">Size</th>
                    <th className="text-left py-3 px-3 font-semibold">Pattern/Code</th>
                    <th className="text-left py-3 px-3 font-semibold">Type</th>
                    {zones.map(z => (
                      <th key={z.id} className="text-right py-3 px-3 font-semibold">{z.zone_name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {codePrices.map(price => (
                    <tr 
                      key={price.id} 
                      onClick={() => setViewingPrice(price)}
                      className={`cursor-pointer transition-colors ${price.is_black ? 'bg-gray-900 text-white hover:bg-gray-800' : 'hover:bg-blue-50'}`}
                    >
                      <td className="py-3 px-3">{price.size_category_name || '-'}</td>
                      <td className="py-3 px-3">
                        <span className="font-mono font-bold">{price.code_pattern}</span>
                        {price.description && <span className={`ml-2 text-xs ${price.is_black ? 'text-gray-400' : 'text-gray-500'}`}>({price.description})</span>}
                      </td>
                      <td className="py-3 px-3">{price.tile_type_name || '-'}</td>
                      {zones.map(z => (
                        <td key={z.id} className="py-3 px-3 text-right font-medium">
                          {price.zone_prices?.[z.id] ? `₦${price.zone_prices[z.id].toLocaleString()}` : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Zone Detail Modal */}
      {viewingZone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Zone Details</h3>
              <button onClick={() => setViewingZone(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Zone Name</label>
                <p className="text-lg font-medium text-gray-900">{viewingZone.zone_name}</p>
              </div>
              {viewingZone.zone_code && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Zone Code</label>
                  <p className="text-lg font-mono font-medium text-blue-600">{viewingZone.zone_code}</p>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">States ({viewingZone.states?.length || 0})</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {viewingZone.states?.length > 0 ? (
                    viewingZone.states.map(state => (
                      <span key={state} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">{state}</span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No states assigned</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 p-4 border-t bg-gray-50">
              <button 
                onClick={() => { editZone(viewingZone); setViewingZone(null); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                Edit Zone
              </button>
              <button 
                onClick={() => { handleDeleteZone(viewingZone); setViewingZone(null); }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price Detail Modal */}
      {viewingPrice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className={`flex items-center justify-between p-4 border-b ${viewingPrice.is_black ? 'bg-gray-900' : ''}`}>
              <h3 className={`text-lg font-semibold ${viewingPrice.is_black ? 'text-white' : 'text-gray-900'}`}>Price Details</h3>
              <button onClick={() => setViewingPrice(null)} className={`p-2 rounded-lg ${viewingPrice.is_black ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className={`p-4 space-y-4 ${viewingPrice.is_black ? 'bg-gray-900 text-white' : ''}`}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs uppercase tracking-wide ${viewingPrice.is_black ? 'text-gray-400' : 'text-gray-500'}`}>Code Pattern</label>
                  <p className="text-xl font-mono font-bold">{viewingPrice.code_pattern}</p>
                </div>
                {viewingPrice.description && (
                  <div>
                    <label className={`text-xs uppercase tracking-wide ${viewingPrice.is_black ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                    <p className="font-medium">{viewingPrice.description}</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs uppercase tracking-wide ${viewingPrice.is_black ? 'text-gray-400' : 'text-gray-500'}`}>Size Category</label>
                  <p className="font-medium">{viewingPrice.size_category_name || '-'}</p>
                </div>
                <div>
                  <label className={`text-xs uppercase tracking-wide ${viewingPrice.is_black ? 'text-gray-400' : 'text-gray-500'}`}>Tile Type</label>
                  <p className="font-medium">{viewingPrice.tile_type_name || '-'}</p>
                </div>
              </div>
              {viewingPrice.is_black && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg">
                  <span className="w-3 h-3 bg-black rounded-full border border-gray-600"></span>
                  <span className="text-sm text-gray-300">Black Tile (Premium)</span>
                </div>
              )}
              <div>
                <label className={`text-xs uppercase tracking-wide ${viewingPrice.is_black ? 'text-gray-400' : 'text-gray-500'}`}>Zone Prices</label>
                <div className={`mt-2 rounded-lg border ${viewingPrice.is_black ? 'border-gray-700' : 'border-gray-200'} overflow-hidden`}>
                  {zones.map(zone => (
                    <div key={zone.id} className={`flex justify-between items-center px-4 py-3 ${viewingPrice.is_black ? 'border-b border-gray-700 last:border-0' : 'border-b last:border-0'}`}>
                      <span className={viewingPrice.is_black ? 'text-gray-300' : 'text-gray-600'}>{zone.zone_name}</span>
                      <span className="font-semibold">
                        {viewingPrice.zone_prices?.[zone.id] ? `₦${viewingPrice.zone_prices[zone.id].toLocaleString()}` : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 p-4 border-t bg-gray-50">
              <button 
                onClick={() => { editPrice(viewingPrice); setViewingPrice(null); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                Edit Price
              </button>
              <button 
                onClick={() => { handleDeletePrice(viewingPrice); setViewingPrice(null); }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPricing;
